import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Calendar, Sparkles, ArrowRight, MessageSquare, Mail } from 'lucide-react';
import AnimatedCounter from "../components/AnimatedCounter";
import TrailButton from "../components/TrailButton";
import SEO from "../components/SEO";
import MiniROICalculator from "../components/MiniROICalculator";
import { openBookingModal } from "../components/BookingModal";
import { PAGE_SEO, ORG_SCHEMA, SOFTWARE_SCHEMA } from "../seo-data";

const NewHome = () => {
  const observerRef = useRef(null);
  const [displayText, setDisplayText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;
  
  const cycleWords = ['Voice Agents.', 'Lead Follow-Up.', 'Booking Systems.', 'Customer Support.', 'Workflow Automation.', 'Your Business.'];

  useEffect(() => {
    if (isMobile) {
      document.querySelectorAll('.reveal').forEach(el => {
        el.classList.add('visible');
      });
    } else {
      observerRef.current = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observerRef.current.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08 });

      document.querySelectorAll('.reveal:not(.visible)').forEach(el => {
        observerRef.current.observe(el);
      });
    }

    if (window.iFrameResize) {
      window.iFrameResize({ log: false, checkOrigin: false }, '#testimonialto-skye-ai');
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    const currentWord = cycleWords[wordIndex];
    
    const typeSpeed = isDeleting ? 80 : 120;
    const pauseTime = isDeleting ? 0 : 2000;
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), pauseTime);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % cycleWords.length);
        }
      }
    }, typeSpeed);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, wordIndex]);

  const featureTeaser = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "AI Voice Agents",
      description: "Custom-built voice agents that answer calls, qualify leads, and book appointments 24/7 — so your team never misses an opportunity.",
      link: "/features"
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Smart Follow-Up",
      description: "Automated text, email, and voice follow-up that engages every lead instantly — day or night — until they convert.",
      link: "/features"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Workflow Automation",
      description: "End-to-end automation that connects your tools, routes leads, books appointments, and keeps your pipeline moving on autopilot.",
      link: "/features"
    }
  ];

  return (
    <div className="new-home-page">
      <SEO {...PAGE_SEO.home} schema={ORG_SCHEMA} />
      {/* HERO */}
      <section className="hero">
        <div className="hero-mesh-mobile">
          <div className="mesh-blob mesh-blob-1"></div>
          <div className="mesh-blob mesh-blob-2"></div>
          <div className="mesh-blob mesh-blob-3"></div>
          <div className="mesh-blob mesh-blob-4"></div>
          <div className="mesh-blob mesh-blob-5"></div>
        </div>
        <div className="constellation-field">
          <div className="c-cluster c-cluster-1">
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-line cl-1a"></div>
            <div className="c-line cl-1b"></div>
          </div>
          <div className="c-cluster c-cluster-2">
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-line cl-2a"></div>
            <div className="c-line cl-2b"></div>
            <div className="c-line cl-2c"></div>
          </div>
          <div className="c-cluster c-cluster-3">
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-line cl-3a"></div>
            <div className="c-line cl-3b"></div>
          </div>
          <div className="c-cluster c-cluster-4">
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-line cl-4a"></div>
            <div className="c-line cl-4b"></div>
          </div>
          <div className="c-cluster c-cluster-5">
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-node"></div>
            <div className="c-line cl-5a"></div>
            <div className="c-line cl-5b"></div>
          </div>
        </div>

        <div className="hero-aurora">
          <div className="aurora-layer aurora-1"></div>
          <div className="aurora-layer aurora-2"></div>
          <div className="aurora-layer aurora-3"></div>
        </div>
        
        <div className="neural-network">
          <svg className="neural-svg" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
            <g className="neural-connections">
              <line x1="15%" y1="20%" x2="25%" y2="35%" />
              <line x1="25%" y1="35%" x2="10%" y2="55%" />
              <line x1="25%" y1="35%" x2="40%" y2="25%" />
              <line x1="40%" y1="25%" x2="55%" y2="15%" />
              <line x1="55%" y1="15%" x2="70%" y2="22%" />
              <line x1="70%" y1="22%" x2="85%" y2="18%" />
              <line x1="85%" y1="18%" x2="92%" y2="35%" />
              <line x1="70%" y1="22%" x2="75%" y2="45%" />
              <line x1="75%" y1="45%" x2="88%" y2="55%" />
              <line x1="75%" y1="45%" x2="60%" y2="60%" />
              <line x1="10%" y1="55%" x2="20%" y2="75%" />
              <line x1="20%" y1="75%" x2="35%" y2="85%" />
              <line x1="35%" y1="85%" x2="55%" y2="80%" />
              <line x1="55%" y1="80%" x2="60%" y2="60%" />
              <line x1="60%" y1="60%" x2="45%" y2="50%" />
              <line x1="45%" y1="50%" x2="25%" y2="35%" />
              <line x1="88%" y1="55%" x2="95%" y2="70%" />
              <line x1="95%" y1="70%" x2="85%" y2="85%" />
              <line x1="85%" y1="85%" x2="70%" y2="78%" />
              <line x1="70%" y1="78%" x2="55%" y2="80%" />
            </g>
            
            <g className="neural-nodes">
              <circle cx="15%" cy="20%" r="4" className="node node-1" />
              <circle cx="25%" cy="35%" r="6" className="node node-2" />
              <circle cx="10%" cy="55%" r="4" className="node node-3" />
              <circle cx="40%" cy="25%" r="5" className="node node-4" />
              <circle cx="55%" cy="15%" r="4" className="node node-5" />
              <circle cx="70%" cy="22%" r="6" className="node node-6" />
              <circle cx="85%" cy="18%" r="4" className="node node-7" />
              <circle cx="92%" cy="35%" r="5" className="node node-8" />
              <circle cx="75%" cy="45%" r="5" className="node node-9" />
              <circle cx="88%" cy="55%" r="4" className="node node-10" />
              <circle cx="60%" cy="60%" r="6" className="node node-11" />
              <circle cx="45%" cy="50%" r="4" className="node node-12" />
              <circle cx="20%" cy="75%" r="5" className="node node-13" />
              <circle cx="35%" cy="85%" r="4" className="node node-14" />
              <circle cx="55%" cy="80%" r="5" className="node node-15" />
              <circle cx="95%" cy="70%" r="4" className="node node-16" />
              <circle cx="85%" cy="85%" r="5" className="node node-17" />
              <circle cx="70%" cy="78%" r="4" className="node node-18" />
            </g>
            
            <g className="data-pulses">
              <circle r="3" className="pulse pulse-1">
                <animateMotion dur="8s" repeatCount="indefinite" path="M288,216 L480,378 L192,594" />
              </circle>
              <circle r="3" className="pulse pulse-2">
                <animateMotion dur="10s" repeatCount="indefinite" path="M1344,238 L1632,194 L1766,378" />
              </circle>
              <circle r="3" className="pulse pulse-3">
                <animateMotion dur="9s" repeatCount="indefinite" path="M1056,864 L1152,648 L864,540" />
              </circle>
            </g>
          </svg>
        </div>
        
        <div className="hero-grid"></div>
        <div className="hero-mesh"></div>
        <div className="hero-glow hero-glow-1"></div>
        <div className="hero-glow hero-glow-2"></div>
        <div className="hero-glow hero-glow-3"></div>
        
        <div className="hero-content">
          <div className="hero-eyebrow" data-testid="hero-eyebrow">
            Custom AI Buildouts for Business
          </div>
          
          <h1 data-testid="hero-title">
            <span className="hero-title-word">AI Revenue Engine</span><sup className="tm">™</sup>
            <br />
            <span className="accent animated-gradient">
              Built For <span className="typewriter-text">{displayText}</span><span className="cursor">|</span>
            </span>
          </h1>
          
          <p className="hero-sub" data-testid="hero-subtitle">
            We build custom AI systems that handle your communication, follow-up, booking, and workflows — so your team can focus on closing.
          </p>
          
          <div className="hero-buttons">
            <TrailButton>
              <button 
                className="btn-dark btn-glow"
                onClick={openBookingModal}
                data-testid="hero-cta-button"
              >
                Book My AI Growth Planning Call →
              </button>
            </TrailButton>
            <button 
              onClick={() => document.getElementById('ai-demo-section').scrollIntoView({ behavior: 'smooth' })}
              className="btn-outline"
              data-testid="hero-demo-button"
            >
              Talk to Our AI <span className="arrow">↓</span>
            </button>
          </div>

          <div className="hero-guarantee" data-testid="hero-guarantee" style={{ marginTop: '20px', textAlign: 'center', opacity: 0.9 }}>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
              Custom AI voice agents, chatbots, and automations — built and managed for your business.
            </p>
          </div>
        </div>
        
        <div className="hero-social-proof">
          <div className="social-proof-item stat-pop" style={{ animationDelay: '0.2s' }}>
            <div className="proof-number gradient-number">
              $<AnimatedCounter end={987} suffix="K+" duration={2500} />
            </div>
            <div className="proof-label">Revenue Generated</div>
          </div>
          <div className="social-proof-item stat-pop" style={{ animationDelay: '0.4s' }}>
            <div className="proof-number gradient-number">
              <AnimatedCounter end={300} suffix="+" duration={2500} />
            </div>
            <div className="proof-label">Businesses Served</div>
          </div>
          <div className="social-proof-item stat-pop" style={{ animationDelay: '0.6s' }}>
            <div className="proof-number gradient-number">
              <AnimatedCounter end={24} suffix="/7" duration={2500} />
            </div>
            <div className="proof-label">AI Systems Running</div>
          </div>
        </div>
      </section>

      {/* AI DEMO ORB */}
      <section className="ai-demo-section" id="ai-demo-section">
        <div className="ai-demo-bg"></div>
        <div className="container">
          <div className="ai-demo-content reveal">
            <div className="ai-demo-badge">
              <Sparkles className="w-4 h-4" />
              <span>Live AI Demo</span>
            </div>
            <h2 className="ai-demo-title">
              Hear How Our AI <span className="gradient-text-inline">Handles Calls</span>
            </h2>
            <p className="ai-demo-sub">
              Click below and experience a live AI voice agent firsthand. Hear how it answers questions, qualifies leads, and books appointments on your calendar — all automatically.
            </p>
            <div className="ai-demo-orb-wrapper">
              <div className="ai-demo-ambient"></div>
              
              <div className="ai-demo-particles">
                <div className="ai-particle p1"></div>
                <div className="ai-particle p2"></div>
                <div className="ai-particle p3"></div>
                <div className="ai-particle p4"></div>
                <div className="ai-particle p5"></div>
                <div className="ai-particle p6"></div>
                <div className="ai-particle p7"></div>
                <div className="ai-particle p8"></div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', zIndex: 5 }}>
                <iframe 
                  src="https://iframes.ai/o/1772746151894x296210653170892800?color=932efb&icon="
                  allow="microphone"
                  style={{ width: '100%', height: '200px', border: 'none' }}
                  title="Skye AI Voice Demo"
                />
              </div>
            </div>
            
            <div className="orb-cta">
              <div className="orb-cta-arrow">↑</div>
              <span>Click the orb to start talking</span>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="stats-strip">
        <div className="container">
          <div className="stats-strip-grid reveal">
            <div className="stat-strip-item">
              <div className="stat-strip-value">
                $<AnimatedCounter end={987} suffix="K+" duration={2000} />
              </div>
              <div className="stat-strip-label">Revenue Generated for Clients</div>
            </div>
            <div className="stat-strip-divider"></div>
            <div className="stat-strip-item">
              <div className="stat-strip-value">
                <AnimatedCounter end={1443} suffix="%" duration={2000} />
              </div>
              <div className="stat-strip-label">Peak Client ROAS</div>
            </div>
            <div className="stat-strip-divider"></div>
            <div className="stat-strip-item">
              <div className="stat-strip-value">
                <AnimatedCounter end={300} suffix="+" duration={2000} />
              </div>
              <div className="stat-strip-label">Businesses Served</div>
            </div>
            <div className="stat-strip-divider"></div>
            <div className="stat-strip-item">
              <div className="stat-strip-value">
                <AnimatedCounter end={30} duration={2000} /> days
              </div>
              <div className="stat-strip-label">To Go Live</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS SKYE AI */}
      <section className="what-is-section">
        <div className="container">
          <div className="what-is-grid">
            <div className="what-is-content reveal">
              <div className="eyebrow">Built for Growing Businesses</div>
              <h2 className="section-h">We Build AI Systems That<br /><span className="gradient-text-inline">Run Your Business</span></h2>
              <p className="what-is-text">
                Skye AI isn't another SaaS tool you have to figure out. We build custom AI systems — voice agents, chatbots, follow-up sequences, and workflow automations — tailored to the way your business actually operates.
              </p>
              <p className="what-is-text">
                Whether you're drowning in leads that never get followed up, losing customers to slow response times, or wasting hours on manual tasks — we build the AI that fixes it. Done for you, managed by our team, running 24/7.
              </p>
              <Link to="/how-it-works" className="what-is-link">
                See how it works <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="what-is-visual reveal">
              <div className="dashboard-preview">
                <div className="dash-topbar">
                  <div className="dash-dots">
                    <div className="dot dot-r"></div>
                    <div className="dot dot-y"></div>
                    <div className="dot dot-g"></div>
                  </div>
                  <div className="dash-title">Revenue Engine Dashboard</div>
                  <div style={{ width: '52px' }}></div>
                </div>
                <div className="dash-body">
                  <div className="dash-metric-row">
                    <div className="dash-metric">
                      <span className="dash-metric-label">Revenue Generated</span>
                      <span className="dash-metric-value">$127,400</span>
                      <span className="dash-metric-change positive">↑ 34%</span>
                    </div>
                    <div className="dash-metric">
                      <span className="dash-metric-label">Appointments Booked</span>
                      <span className="dash-metric-value">89</span>
                      <span className="dash-metric-change positive">↑ 28%</span>
                    </div>
                  </div>
                  <div className="dash-chart">
                    <div className="chart-bar" style={{ height: '40%' }}></div>
                    <div className="chart-bar" style={{ height: '55%' }}></div>
                    <div className="chart-bar" style={{ height: '45%' }}></div>
                    <div className="chart-bar" style={{ height: '70%' }}></div>
                    <div className="chart-bar" style={{ height: '60%' }}></div>
                    <div className="chart-bar" style={{ height: '85%' }}></div>
                    <div className="chart-bar active" style={{ height: '95%' }}></div>
                  </div>
                  <div className="dash-pills">
                    <span className="d-pill pill-green">Engine Running</span>
                    <span className="d-pill pill-pink">23 leads in pipeline</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES TEASER */}
      <section className="features-teaser-section">
        <div className="container">
          <div className="features-teaser-header reveal">
            <div className="eyebrow">Core Capabilities</div>
            <h2 className="section-h">Everything You Need to<br />Scale Your Business with AI</h2>
          </div>
          <div className="features-teaser-grid">
            {featureTeaser.map((feature, index) => (
              <Link 
                to={feature.link} 
                key={feature.title} 
                className="feature-teaser-card reveal"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="feature-teaser-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <span className="feature-teaser-link">
                  Learn more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
          <div className="features-teaser-cta reveal">
            <Link to="/features" className="btn-outline-gradient">
              View All Features <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="industries-section">
        <div className="container">
          <div className="industries-header reveal">
            <div className="eyebrow">What We Build</div>
            <h2 className="section-h">Custom AI For<br /><span className="gradient-text-inline">Every Part of Your Business</span></h2>
            <p className="section-p">From first touch to closed deal — we automate the workflows that matter most.</p>
          </div>
          <div className="industries-layout">
            <div className="industries-grid reveal">
              {[
                { icon: '🤖', name: 'AI Voice Agents', slug: 'voice' },
                { icon: '💬', name: 'AI Chatbots', slug: 'chat' },
                { icon: '📞', name: 'Lead Follow-Up', slug: 'followup' },
                { icon: '📅', name: 'Appointment Booking', slug: 'booking' },
                { icon: '⚡', name: 'Workflow Automation', slug: 'workflow' },
                { icon: '📊', name: 'CRM Integration', slug: 'crm' },
                { icon: '📧', name: 'Email & SMS Sequences', slug: 'sequences' },
                { icon: '🎯', name: 'Lead Qualification', slug: 'qualify' },
              ].map((item) => (
                <div
                  key={item.slug}
                  className="industry-tile"
                  style={{ '--tile-color': '#7c3aed', cursor: 'default' }}
                >
                  <span style={{ fontSize: '20px' }}>{item.icon}</span>
                  <span>{item.name}</span>
                </div>
              ))}
            </div>
            <div className="industries-calc reveal">
              <MiniROICalculator />
            </div>
          </div>
        </div>
      </section>

      {/* WHY US - TEAM SECTION */}
      <section className="team-orbit-section" id="why-us">
        <div className="team-orbit-bg"></div>
        <div className="orbit-particles">
          <div className="particle particle-1"></div>
          <div className="particle particle-2"></div>
          <div className="particle particle-3"></div>
          <div className="particle particle-4"></div>
          <div className="particle particle-5"></div>
          <div className="particle particle-6"></div>
        </div>
        <div className="container">
          <div className="team-orbit-header reveal">
            <div className="eyebrow eyebrow-light">Why Skye AI</div>
            <h2 className="section-h section-h-light">
              Your Dedicated 5-Person<br />
              <span className="gradient-text-hero">Growth Team</span>
            </h2>
            <p className="team-orbit-sub">
              Not software. Not another agency. A complete team that builds and manages your custom AI systems so your business runs smarter.
            </p>
          </div>

          <div className="orbit-container reveal">
            <div className="orbit-bg-ring orbit-bg-ring-1"></div>
            <div className="orbit-bg-ring orbit-bg-ring-2"></div>
            <div className="orbit-bg-ring orbit-bg-ring-3"></div>
            
            <div className="orbit-center">
              <div className="orbit-center-glow"></div>
              <div className="orbit-center-inner">
                <span className="orbit-you">YOU</span>
                <span className="orbit-you-sub">Show up & sell</span>
              </div>
              <div className="orbit-ring orbit-ring-1"></div>
              <div className="orbit-ring orbit-ring-2"></div>
              <div className="orbit-ring orbit-ring-3"></div>
            </div>
            
            <div className="orbit-member orbit-pos-1">
              <div className="orbit-member-glow glow-pink"></div>
              <div className="orbit-member-dot"></div>
              <div className="orbit-member-info">
                <span className="orbit-role">Success Manager</span>
                <ul className="orbit-tasks">
                  <li>Your dedicated point of contact</li>
                  <li>Runs weekly strategy calls</li>
                  <li>Keeps your AI systems optimized</li>
                </ul>
              </div>
            </div>
            
            <div className="orbit-member orbit-pos-2">
              <div className="orbit-member-glow glow-cyan"></div>
              <div className="orbit-member-dot dot-cyan"></div>
              <div className="orbit-member-info">
                <span className="orbit-role">AI Engineer</span>
                <ul className="orbit-tasks">
                  <li>Builds custom voice agents &amp; chatbots</li>
                  <li>Designs intelligent follow-up flows</li>
                  <li>Books appointments on your calendar</li>
                </ul>
              </div>
            </div>
            
            <div className="orbit-member orbit-pos-3">
              <div className="orbit-member-glow glow-purple"></div>
              <div className="orbit-member-dot dot-purple"></div>
              <div className="orbit-member-info">
                <span className="orbit-role">Automation Specialist</span>
                <ul className="orbit-tasks">
                  <li>Connects your CRM &amp; tools</li>
                  <li>Sets up automated lead routing</li>
                  <li>Integrates with your existing stack</li>
                </ul>
              </div>
            </div>
            
            <div className="orbit-member orbit-pos-4">
              <div className="orbit-member-glow glow-orange"></div>
              <div className="orbit-member-dot dot-orange"></div>
              <div className="orbit-member-info">
                <span className="orbit-role">Growth Strategist</span>
                <ul className="orbit-tasks">
                  <li>Runs paid campaigns when needed</li>
                  <li>Targets high-intent prospects</li>
                  <li>Manages spend for maximum ROI</li>
                </ul>
              </div>
            </div>
            
            <div className="orbit-member orbit-pos-5">
              <div className="orbit-member-glow glow-green"></div>
              <div className="orbit-member-dot dot-green"></div>
              <div className="orbit-member-info">
                <span className="orbit-role">Growth Analyst</span>
                <ul className="orbit-tasks">
                  <li>Tracks all leads, conversions &amp; revenue</li>
                  <li>A/B tests messaging &amp; flows</li>
                  <li>Optimizes for more booked appointments</li>
                </ul>
              </div>
            </div>

            <svg className="orbit-connections" viewBox="0 0 800 500">
              <defs>
                <linearGradient id="lineGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(244, 114, 182, 0)" />
                  <stop offset="50%" stopColor="rgba(244, 114, 182, 0.5)" />
                  <stop offset="100%" stopColor="rgba(244, 114, 182, 0)" />
                </linearGradient>
                <linearGradient id="lineGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(6, 182, 212, 0)" />
                  <stop offset="50%" stopColor="rgba(6, 182, 212, 0.5)" />
                  <stop offset="100%" stopColor="rgba(6, 182, 212, 0)" />
                </linearGradient>
                <linearGradient id="lineGradVertical" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgba(244, 114, 182, 0)" />
                  <stop offset="50%" stopColor="rgba(244, 114, 182, 0.5)" />
                  <stop offset="100%" stopColor="rgba(244, 114, 182, 0)" />
                </linearGradient>
              </defs>
              <path className="connection-line line-1" d="M400,70 C400,120 400,180 400,250" stroke="url(#lineGradVertical)" />
              <path className="connection-line line-2" d="M700,150 Q550,200 400,250" stroke="url(#lineGrad2)" />
              <path className="connection-line line-3" d="M650,400 Q525,325 400,250" stroke="url(#lineGrad1)" />
              <path className="connection-line line-4" d="M150,400 Q275,325 400,250" stroke="url(#lineGrad2)" />
              <path className="connection-line line-5" d="M100,150 Q250,200 400,250" stroke="url(#lineGrad1)" />
            </svg>
          </div>

          <div className="team-orbit-footer reveal">
            <div className="orbit-tagline">
              <div className="tagline-icon">
                <Sparkles className="w-5 h-5" />
              </div>
              <p>Zero setup. Zero learning curve. Your AI systems just work — we handle everything.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS WALL */}
      <section className="testimonials-wall-section">
        <div className="container">
          <div className="section-header reveal">
            <div className="eyebrow">Social Proof</div>
            <h2 className="section-h">What Business Owners Say</h2>
            <p className="section-p">Real results from real businesses using custom AI systems built by Skye AI</p>
          </div>
          <div className="testimonials-embed reveal">
            <iframe 
              id='testimonialto-skye-ai'
              src="https://embed-v2.testimonial.to/w/skye-ai?id=workflow-showcase-3"
              frameBorder="0"
              scrolling="no"
              width="100%"
              style={{ minHeight: '500px' }}
              title="Skye AI Testimonials"
            />
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-inner">
            <div className="contact-icon">
              <Mail className="w-8 h-8" />
            </div>
            <h2 className="section-h">Get In Touch</h2>
            <p className="section-p">
              Have questions about how custom AI can scale your business? We'd love to hear from you.
            </p>
            <a href="mailto:info@skyeai.io" className="contact-email-btn">
              <Mail className="w-5 h-5" />
              info@skyeai.io
            </a>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="cta-section" id="cta">
        <div className="cta-bg-elements">
          <div className="cta-glow cta-glow-1"></div>
          <div className="cta-glow cta-glow-2"></div>
        </div>
        <div className="cta-inner reveal">
          <div className="eyebrow">Get Started</div>
          <h2 className="section-h">Ready to Automate Your Business With<br /><span className="gradient-text-inline">Custom AI</span>?</h2>
          <p className="section-p" style={{ margin: '0 auto 40px', textAlign: 'center' }}>
            Voice agents, chatbots, follow-up sequences, and workflow automation — built and managed for you. <strong>Limited to 10 new clients per month.</strong>
          </p>
          <div className="cta-buttons">
            <button onClick={openBookingModal} className="btn-gradient btn-lg" data-testid="cta-book-call">
              Book My AI Growth Planning Call <ArrowRight className="w-5 h-5" />
            </button>
            <Link to="/how-it-works" className="btn-outline-light">
              Learn How It Works
            </Link>
          </div>
          <div className="cta-trust-row">
            <span>Live in 30 days</span>
            <span>Done-for-you setup</span>
            <span>300+ businesses served</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewHome;
